/**
 * Halftone P loader: framework-agnostic canvas animation, from the 2026 Pulse branding assets
 * (`logo_standalone_animated.js`).
 *
 * Logo → dots disassemble → spinning sphere → colour-sorted volume waveform → back to logo.
 * Drawn on a 28×28 logical grid (the logo's own viewBox) and scaled to the canvas's CSS size.
 *
 *   const loader = createHalftoneLoader(canvasEl, { speed: 1 });
 *   loader.destroy(); // when done
 */

// ---------------------------------------------------------------- animation core
/** `[x, y, radius, color]` per logo dot, on the 28×28 grid. */
type LogoDot = readonly [x: number, y: number, r: number, color: string];

const DOTS: readonly LogoDot[] = [
  [8.5, 1.5, 1.45, '#A5B85F'],
  [13.5, 1.5, 1.49, '#66B7CC'],
  [18.5, 1.5, 1.36, '#5699CC'],
  [3.5, 6.5, 1.54, '#ECB019'],
  [8.5, 6.5, 1.81, '#FFAB05'],
  [13.5, 6.5, 1.88, '#C9C353'],
  [18.5, 6.5, 1.69, '#66B7CC'],
  [23.5, 6.5, 1.36, '#5699CC'],
  [3.5, 11.5, 1.54, '#FEA012'],
  [8.5, 11.5, 1.88, '#FFAB05'],
  [13.5, 11.5, 1.88, '#FFAB05'],
  [18.5, 11.5, 1.88, '#C9C353'],
  [23.5, 11.5, 1.49, '#66B7CC'],
  [3.5, 16.5, 1.54, '#FD882E'],
  [8.5, 16.5, 1.88, '#FE961E'],
  [13.5, 16.5, 1.88, '#FFAB05'],
  [18.5, 16.5, 1.7, '#FFAB05'],
  [23.5, 16.5, 0.95, '#A5B85F'],
  [3.5, 21.5, 1.41, '#FB704A'],
  [8.5, 21.5, 1.64, '#FC8334'],
  [13.5, 21.5, 1.41, '#FE9C17'],
  [18.5, 21.5, 0.95, '#EEB016'],
  [3.66, 26.5, 1.26, '#FA5B62'],
  [8.65, 26.5, 1.26, '#FC7643'],
  [13.5, 26.5, 1.1, '#FD9222'],
];

const GRID = 28; // logical grid edge
const CX = 14;
const CY = 14;
const PER = 2; // particles per logo dot
const PR = 0.62; // uniform particle radius (logo units)
const SPHERE_R = 10.5;
const COLS = 10;
const ROWS = 5; // color-sorted grid, filled column by column in hue order

// Timeline (seconds)
const PHASE_DEFS = [
  { key: 'hold', d: 0.6 },
  { key: 'split', d: 1.5 },
  { key: 'sphere', d: 1.4 },
  { key: 'sort', d: 1.1 },
  { key: 'wave', d: 2.8 },
  { key: 'back', d: 1.6 },
] as const;

type PhaseKey = (typeof PHASE_DEFS)[number]['key'];

const PHASES = PHASE_DEFS.reduce<{ key: PhaseKey; d: number; start: number }[]>((acc, def) => {
  const prev = acc[acc.length - 1];
  acc.push({ ...def, start: prev ? prev.start + prev.d : 0 });
  return acc;
}, []);
const LAST_PHASE = PHASES[PHASES.length - 1];
const TOTAL = LAST_PHASE ? LAST_PHASE.start + LAST_PHASE.d : 0;

interface Particle {
  dot: number;
  ox: number;
  oy: number;
  color: string;
  j: number;
  /** Rank in the top-to-bottom ordering. */
  s: number;
  sx: number;
  sy: number;
  sz: number;
  col: number;
  row: number;
  rnd: number;
  r2: number;
  r3: number;
  r4: number;
}

const clamp = (v: number, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Hue (degrees, red wrapped to just below 0) of a `#rrggbb` color. */
function hue(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const d = mx - mn;
  let h = d === 0 ? 0 : mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  if (h > 300) h -= 360;
  return h;
}

const GOLD = Math.PI * (3 - Math.sqrt(5));
const pseudoRandom = (seed: number, k: number) => {
  const v = Math.sin((seed + 1) * k) * 43758.5453;
  return v - Math.floor(v);
};

function buildParticles() {
  const base = DOTS.flatMap((dot, i) =>
    Array.from({ length: PER }, (_, j) => {
      const a = j * Math.PI + Math.PI / 4;
      return {
        dot: i,
        ox: Math.cos(a) * dot[2] * 0.4,
        oy: Math.sin(a) * dot[2] * 0.4,
        color: dot[3],
        j,
      };
    }),
  );
  // Order top-to-bottom so the color gradient carries into sphere bands and waveform columns
  const order = base
    .map((_, i) => i)
    .sort((a, b) => {
      const A = base[a];
      const B = base[b];
      const da = A && DOTS[A.dot];
      const db = B && DOTS[B.dot];
      if (!A || !B || !da || !db) return 0;
      return da[1] - db[1] || da[0] - db[0] || A.j - B.j;
    });
  const rankOf = new Map(order.map((pi, s) => [pi, s]));

  // Column order: logo dots sorted by hue (red → orange → yellow → green → blue), same colors adjacent
  const dotRank = new Map(
    DOTS.map((_, i) => i)
      .sort((a, b) => {
        const da = DOTS[a];
        const db = DOTS[b];
        if (!da || !db) return 0;
        return hue(da[3]) - hue(db[3]) || da[3].localeCompare(db[3]) || a - b;
      })
      .map((di, k) => [di, k]),
  );

  const n = base.length;
  return base.map((p, pi): Particle => {
    const s = rankOf.get(pi) ?? 0;
    const y = 1 - (2 * (s + 0.5)) / n;
    const rad = Math.sqrt(1 - y * y);
    const phi = s * GOLD;
    const k = (dotRank.get(p.dot) ?? 0) * PER + p.j;
    return {
      ...p,
      s,
      sx: rad * Math.cos(phi),
      sy: y,
      sz: rad * Math.sin(phi),
      col: Math.floor(k / ROWS),
      row: k % ROWS,
      rnd: ((s * 7919) % 97) / 97,
      r2: pseudoRandom(s, 12.9898),
      r3: pseudoRandom(s, 78.233),
      r4: pseudoRandom(s, 39.425),
    };
  });
}

const parts = buildParticles();
const N = parts.length;

const COL_COLORS: string[][] = Array.from({ length: COLS }, () => []);
parts.forEach((p) => {
  const colColors = COL_COLORS[p.col];
  if (colColors) colColors[p.row] = p.color;
});

interface Pos {
  x: number;
  y: number;
  z: number;
  rs?: number;
}

// State positions -> {x, y, z}
function logoPos(p: Particle, k = 1): Pos {
  const d = DOTS[p.dot] ?? [CX, CY, 0, ''];
  return { x: d[0] + p.ox * k, y: d[1] + p.oy * k, z: 0 };
}
function spherePos(p: Particle, t: number): Pos {
  const A = t * 1.9;
  const T = 0.38;
  const x = p.sx * Math.cos(A) + p.sz * Math.sin(A);
  let z = -p.sx * Math.sin(A) + p.sz * Math.cos(A);
  const y = p.sy * Math.cos(T) - z * Math.sin(T);
  z = p.sy * Math.sin(T) + z * Math.cos(T);
  return { x: CX + x * SPHERE_R, y: CY + y * SPHERE_R, z };
}
// Halftone waveform: dots sit on a fixed grid; their size encodes distance to a travelling sine
const WX = 2.1;
const WY = 1.75;
const W_RMAX = 0.68;
const W_RMIN = 0.12;
// Volume level per column (0..1): a few out-of-phase sines per bar, like a live audio meter
const W_SPEED = 0.85; // waveform tempo
function level(c: number, time: number) {
  const t = time * W_SPEED;
  // Two slow, broad travelling waves: neighbouring bars stay close in height
  const v = 0.6 * Math.sin(c * 0.9 - t * 3.2) + 0.4 * Math.sin(c * 0.55 + t * 2.1 + 1.3);
  return clamp(0.1 + 0.9 * Math.pow(0.5 + 0.5 * v, 1.15));
}
function gridPos(p: Particle): Pos {
  return {
    x: CX + (p.col - (COLS - 1) / 2) * WX,
    y: CY + (p.row - (ROWS - 1) / 2) * WY,
    z: 0,
    rs: 1,
  };
}
// amt 0 = plain sorted grid, 1 = full volume bars. Bars grow out from the centre row.
function wavePos(p: Particle, t: number, amt = 1): Pos {
  const g = gridPos(p);
  const half = level(p.col, t) * (ROWS / 2); // bar half-height in rows
  const dist = Math.abs(p.row - (ROWS - 1) / 2); // this dot's distance from centre row
  const lit = clamp(half - dist + 0.5); // 1 inside the bar, soft at its edge
  const r = W_RMIN + (W_RMAX - W_RMIN) * lit;
  g.rs = lerp(1, r / PR, amt);
  return g;
}
function swirl(pos: Pos, ang: number): Pos {
  const dx = pos.x - CX;
  const dy = pos.y - CY;
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  return { x: CX + dx * c - dy * s, y: CY + dx * s + dy * c, z: pos.z, rs: pos.rs };
}
const mix = (a: Pos, b: Pos, t: number): Pos => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  z: lerp(a.z, b.z, t),
  rs: lerp(a.rs ?? 1, b.rs ?? 1, t),
});
const local = (p: number, d: number, spread = 0.35) => ease(clamp((p - d * spread) / (1 - spread)));

interface FrameDot {
  x: number;
  y: number;
  r: number;
  c: string;
  a: number;
}
interface FramePart extends Pos {
  /** 0 = flat, 1 = fully on the sphere (drives depth shading). */
  w: number;
  c: string;
  a: number;
}
interface FrameBar {
  x: number;
  y1: number;
  y2: number;
  gy1: number;
  gy2: number;
  m: number;
  colors: string[];
}
interface Frame {
  dots: FrameDot[];
  parts: FramePart[];
  bars?: FrameBar[];
}

const logoDots = (scale: (r: number) => number): FrameDot[] =>
  DOTS.map((d) => ({ x: d[0], y: d[1], r: scale(d[2]), c: d[3], a: 1 }));

// Compute a frame at time t
function frame(time: number): Frame {
  const t = ((time % TOTAL) + TOTAL) % TOTAL;
  const ph = PHASES.find((p) => t >= p.start && t < p.start + p.d) ?? LAST_PHASE;
  const out: Frame = { dots: [], parts: [] };
  if (!ph) return out;
  const pr = (t - ph.start) / ph.d;

  if (ph.key === 'hold') {
    out.dots = logoDots((r) => r);
    return out;
  }
  if (ph.key === 'split') {
    const shrink = 0.5 - 0.5 * Math.cos(Math.PI * clamp(pr / 0.6));
    out.dots = logoDots((r) => r * (1 - shrink));
    parts.forEach((p) => {
      // Softer sine easing, wider stagger and a lighter swirl for a gentle lift-off
      const e = 0.5 - 0.5 * Math.cos(Math.PI * clamp((pr - (p.s / N) * 0.45) / 0.55));
      let pos = mix(logoPos(p), spherePos(p, t), e);
      pos = swirl(pos, Math.sin(Math.PI * e) * 0.5);
      out.parts.push({ ...pos, w: e, c: p.color, a: clamp(pr / 0.25) });
    });
    return out;
  }
  if (ph.key === 'sphere') {
    parts.forEach((p) => out.parts.push({ ...spherePos(p, t), w: 1, c: p.color, a: 1 }));
    return out;
  }
  if (ph.key === 'sort') {
    parts.forEach((p) => {
      const e = local(pr, (p.col + p.row * 0.15) / COLS, 0.4);
      let pos = mix(spherePos(p, t), wavePos(p, t, 1), e);
      pos = swirl(pos, -Math.sin(Math.PI * e) * 0.6);
      out.parts.push({ ...pos, w: 1 - e, c: p.color, a: 1 });
    });
    return out;
  }
  if (ph.key === 'wave') {
    parts.forEach((p) => out.parts.push({ ...wavePos(p, t, 1), w: 0, c: p.color, a: 1 }));
    // When a bar peaks, its dots fuse into one solid column
    const edge = clamp(pr / 0.08) * clamp((1 - pr) / 0.1);
    const bars: FrameBar[] = [];
    for (let c = 0; c < COLS; c++) {
      // Fuse once the bar passes 50% of its apex; the solid bar spans the lit dots
      const lv = level(c, t);
      const m = ease(clamp((lv - 0.25) / 0.08)) * edge;
      if (m <= 0.01) continue;
      const x = CX + (c - (COLS - 1) / 2) * WX;
      const ext = clamp(lv * (ROWS / 2) - 0.5, 0, (ROWS - 1) / 2) * WY;
      const top = CY - ((ROWS - 1) / 2) * WY;
      const bot = CY + ((ROWS - 1) / 2) * WY;
      bars.push({
        x,
        y1: CY - ext,
        y2: CY + ext,
        gy1: top,
        gy2: bot,
        m,
        colors: COL_COLORS[c] ?? [],
      });
    }
    out.bars = bars;
    return out;
  }
  // back to logo
  parts.forEach((p) => {
    // Each dot takes its own route home: random start, duration, arc bend and swirl direction
    const dur = 0.42 + 0.22 * p.r2;
    const start = p.rnd * (0.86 - dur);
    const e = 0.5 - 0.5 * Math.cos(Math.PI * clamp((pr - start) / dur));
    const from = wavePos(p, t);
    const to = logoPos(p, 1 - 0.4 * e);
    const pos = mix(from, to, e);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    const bend = Math.sin(Math.PI * e) * (p.r3 * 2 - 1) * Math.min(5, len * 0.45);
    pos.x += (-dy / len) * bend;
    pos.y += (dx / len) * bend;
    const swirled = swirl(pos, Math.sin(Math.PI * e) * (p.r4 - 0.5) * 0.7);
    out.parts.push({ ...swirled, w: 0, c: p.color, a: 1 - clamp((pr - 0.8) / 0.2) });
  });
  const grow = 0.5 - 0.5 * Math.cos(Math.PI * clamp((pr - 0.45) / 0.55));
  out.dots = logoDots((r) => r * grow);
  return out;
}

interface View {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  scale: number;
}

function draw(v: View, f: Frame) {
  const { ctx, canvas, scale } = v;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const ps = f.parts.slice().sort((a, b) => a.z - b.z);
  for (const p of ps) {
    const depth = (1 + p.w * 0.25) * (1 + p.w * p.z * 0.38); // sphere dots 25% larger, plus depth scaling
    ctx.globalAlpha = p.a * (1 - p.w * (1 - (p.z + 1) / 2) * 0.55);
    ctx.fillStyle = p.c;
    ctx.beginPath();
    ctx.arc(
      p.x * scale,
      p.y * scale,
      Math.max(0, PR * (p.rs ?? 1) * depth * scale),
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
  for (const b of f.bars ?? []) {
    const g = ctx.createLinearGradient(0, b.gy1 * scale, 0, b.gy2 * scale);
    b.colors.forEach((c, i) => g.addColorStop(i / (b.colors.length - 1), c));
    ctx.strokeStyle = g;
    ctx.lineCap = 'round';
    ctx.lineWidth = 2 * W_RMAX * b.m * scale;
    ctx.beginPath();
    ctx.moveTo(b.x * scale, b.y1 * scale);
    ctx.lineTo(b.x * scale, b.y2 * scale);
    ctx.stroke();
  }
  for (const d of f.dots) {
    if (d.r <= 0.01) continue;
    ctx.globalAlpha = d.a;
    ctx.fillStyle = d.c;
    ctx.beginPath();
    ctx.arc(d.x * scale, d.y * scale, d.r * scale, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------- public API

/** Length of one loop at speed 1, in seconds. */
export const HALFTONE_LOADER_DURATION = TOTAL;

export interface HalftoneLoaderOptions {
  /** Playback rate (default 1). */
  speed?: number;
  /** Start playing immediately (default true). */
  autoplay?: boolean;
  /** Show the static logo when the OS asks for reduced motion (default true). */
  respectReducedMotion?: boolean;
  /** Stop rendering while off-screen or the tab is hidden (default true). */
  pauseWhenHidden?: boolean;
}

export interface HalftoneLoader {
  play: () => void;
  pause: () => void;
  readonly playing: boolean;
  setSpeed: (next: number) => void;
  /** Jump to a time in the loop (seconds) and redraw. */
  seek: (seconds: number) => void;
  destroy: () => void;
}

/**
 * Mount the loader on a <canvas>. Size the canvas with CSS (it should be square);
 * the backing store follows its size and devicePixelRatio automatically.
 */
export function createHalftoneLoader(
  canvas: HTMLCanvasElement,
  options: HalftoneLoaderOptions = {},
): HalftoneLoader {
  const {
    speed: initialSpeed = 1,
    autoplay = true,
    respectReducedMotion = true,
    pauseWhenHidden = true,
  } = options;

  const ctx = canvas.getContext('2d');
  const view = { canvas, ctx, scale: 1 };
  let speed = initialSpeed;
  let t = 0;
  let playing = autoplay;
  let visible = true;
  let raf = 0;
  let last = 0;
  let destroyed = false;

  const motionQuery =
    typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  const reduced = () => respectReducedMotion && !!motionQuery && motionQuery.matches;

  function render() {
    if (view.ctx) draw({ ...view, ctx: view.ctx }, frame(reduced() ? 0 : t));
  }

  function resize() {
    const dpr = Math.max(1, (typeof devicePixelRatio === 'number' && devicePixelRatio) || 1);
    const rect = canvas.getBoundingClientRect();
    const size = Math.max(1, Math.min(rect.width, rect.height) || canvas.width / dpr || GRID);
    const px = Math.round(size * dpr);
    if (canvas.width !== px || canvas.height !== px) {
      canvas.width = px;
      canvas.height = px;
    }
    view.scale = canvas.width / GRID;
    render();
  }

  const shouldRun = () => playing && visible && !reduced() && !destroyed;

  function loop(now: number) {
    raf = 0;
    if (destroyed) return;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
    last = now;
    t = (t + dt * speed) % TOTAL;
    render();
    // Keep `last` across frames; only a fresh start from idle resets it (see `schedule`).
    if (shouldRun()) raf = requestAnimationFrame(loop);
  }

  function schedule() {
    const run = shouldRun();
    if (run && !raf) {
      last = 0;
      raf = requestAnimationFrame(loop);
    }
    if (!run && raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  }

  // Keep the canvas crisp at any size
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  ro?.observe(canvas);

  // Don't burn frames when nobody can see it
  let io: IntersectionObserver | null = null;
  const onVisibility = () => {
    visible = !document.hidden;
    schedule();
  };
  if (pauseWhenHidden) {
    if (typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver((entries) => {
        const latest = entries[entries.length - 1];
        visible = !!latest && latest.isIntersecting && !document.hidden;
        schedule();
      });
      io.observe(canvas);
    }
    document.addEventListener('visibilitychange', onVisibility);
  }
  const onMotionChange = () => {
    render();
    schedule();
  };
  motionQuery?.addEventListener?.('change', onMotionChange);

  resize();
  schedule();

  return {
    play() {
      playing = true;
      schedule();
    },
    pause() {
      playing = false;
      schedule();
    },
    get playing() {
      return playing;
    },
    setSpeed(next) {
      speed = Math.max(0, Number(next) || 0);
    },
    seek(seconds) {
      t = ((seconds % TOTAL) + TOTAL) % TOTAL;
      render();
    },
    destroy() {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
      io?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      motionQuery?.removeEventListener?.('change', onMotionChange);
    },
  };
}
